import { Button, InputText, Loading } from '@/components';
import React, { Fragment } from 'react';
import { Col, Container, Row } from 'reactstrap';
import Search from '@/assets/images/svg/search.svg';
import { FormData, FormErrors, OnChangeFormInput } from '@/hooks';
import { States } from '@/helpers/common/states';
import { ChargeBack } from '@/model/ChargeBack';
import dayjs from 'dayjs';
import { toCurrency } from '@/helpers/masks/toCurrency';
import { CustomTable } from '@/components/Table';
import { FormInputName, ticketColumns, transferColumns } from '../../types';

interface StateProps {
  state: States;
  formData: FormData;
  formErrors: FormErrors;
  chargeBack?: ChargeBack;
}

interface DispatchProps {
  onChangeFormInput: OnChangeFormInput;
  onFilter: () => void;
  onExport: () => void;
}

type Props = StateProps & DispatchProps;

export const ChargebackContainer: React.FC<Props> = ({
  state,
  formData,
  onChangeFormInput,
  formErrors,
  onFilter,
  chargeBack,
  onExport,
}): JSX.Element => {
  const ofuscateAndMaskCPF = (cpf: string): string => {
    const newCpf = cpf.replace(/\D/g, '');
    return `${newCpf.slice(0, 3)}.***.***-${cpf.slice(9)}`;
  };
  const toTicketStatus = (status: number): string => {
    if (status === 0) {
      return 'Ativo';
    }
    if (status === 1) {
      return 'Inativo';
    }
    if (status === 2) {
      return 'Excluido';
    }
    if (status === 3) {
      return 'Traferido';
    }
    return 'N/A';
  };
  const maskCard = (first: string, last: string): string =>
    `${first.slice(0, 4)} ${first.slice(4, 6)}** **** ${last}`;

  return (
    <Fragment>
      <Loading isVisible={state === States.loading} />
      <Container className="mainContainer" fluid={true}>
        <div
          className="filters-grid"
          style={{
            display: 'grid',
            gridTemplateColumns: '2fr 1fr 1fr auto',
            gap: '16px',
            alignItems: 'end',
            marginTop: '16px',
          }}
        >
          <InputText
            name="itemLabel"
            type="text"
            label="Transação"
            placeholder="Informe a transação"
            value={formData[FormInputName.transactionCode]}
            onChange={e => onChangeFormInput(FormInputName.transactionCode)(e.target.value)}
            error={formErrors.transactionCode && formErrors.transactionCode[0]}
          />

          <button
            type="button"
            onClick={onFilter}
            style={{
              cursor: 'pointer',
              height: 50,
              width: 50,
              padding: '0 12px',
              boxSizing: 'border-box',
              border: '1px solid #ccc',
              borderRadius: 5,
              backgroundColor: '#f0f0f0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              alignSelf: 'end',
              marginBottom: 55,
            }}
            aria-label="Buscar"
            title="Buscar"
          >
            <img src={Search} alt="" style={{ width: 24, height: 24 }} />
          </button>
        </div>
        {chargeBack && (
          <Fragment>
            <div id="ExportToPdf">
              <h6>Informações do Pedido</h6>
              <hr />
              <Row>
                <Col md={12}>
                  <label className={'input-label m-0'}>Código do pedido</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>{chargeBack.order.id}</Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>Nome do comprador</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>{chargeBack.order.client.clientName}</Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>CPF do comprador</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>{ofuscateAndMaskCPF(chargeBack.order.client.clientCpf)}</Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>Data da compra</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  {dayjs(chargeBack.order.date).locale('pt-br').format('DD/MM/YYYY [às] HH:mm')}
                </Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>Valor da compra</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>{toCurrency(chargeBack.order.totalValue)}</Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>Ingressos</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <CustomTable
                    columns={ticketColumns}
                    data={chargeBack.order.itens.map(data => ({
                      event: data.event,
                      section: data.section,
                      ticket: data.ticket,
                      status: toTicketStatus(data.statusType),
                      used: data.validatedDate
                        ? dayjs(data.validatedDate).locale('pt-br').format('DD/MM/YYYY [às] HH:mm')
                        : 'Não',
                    }))}
                    numberRowsPerPage={chargeBack.order.itens.length}
                    progressPending={false}
                  />
                </Col>
              </Row>
              <Row style={{ marginTop: '30px' }}>
                <Col md={12}>
                  <h6>Informações da PagSeguro</h6>
                  <hr />
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  <label className={'input-label m-0'}>Código do pedido</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>{chargeBack.pagseguro.pagseguroOrderId}</Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>Código da transação</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>{chargeBack.pagseguro.pagseguroChargeId}</Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>Bandeira</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>{chargeBack.pagseguro.cardBrand}</Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>Cartão</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  {maskCard(
                    chargeBack.pagseguro.cardFirstDigits,
                    chargeBack.pagseguro.cardLastDigits,
                  )}
                </Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>Holder</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>{chargeBack.pagseguro.cardHolder}</Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>CPF Holder</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>{ofuscateAndMaskCPF(chargeBack.pagseguro.cardHolderCpf)}</Col>
              </Row>
              <Row style={{ marginTop: '15px' }}>
                <Col md={12}>
                  <label className={'input-label m-0'}>Autenticação 3DS</label>
                </Col>
              </Row>
              <Row>
                <Col md={12}>
                  {chargeBack.pagseguro.authenticationId
                    ? chargeBack.pagseguro.authenticationId
                    : 'N/A'}
                </Col>
              </Row>
              {chargeBack.cardValidation && chargeBack.cardValidation.validateOrder && (
                <Fragment>
                  <Row style={{ marginTop: '30px' }}>
                    <Col md={12}>
                      <h6>Informações da confirmação do valor cobrado na fatura do cartão</h6>
                      <hr />
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '15px' }}>
                    <Col md={12}>
                      <label className={'input-label m-0'}>Transação</label>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>{chargeBack.cardValidation.validateOrder}</Col>
                  </Row>
                  <Row style={{ marginTop: '15px' }}>
                    <Col md={12}>
                      <label className={'input-label m-0'}>Data da cobrança</label>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      {dayjs(chargeBack.cardValidation.validateCreateDate)
                        .locale('pt-br')
                        .format('DD/MM/YYYY [às] HH:mm:ss.SSS')}
                    </Col>
                  </Row>
                  <Row style={{ marginTop: '15px' }}>
                    <Col md={12}>
                      <label className={'input-label m-0'}>Valor da cobrança</label>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>{toCurrency(chargeBack.cardValidation.validateValue)}</Col>
                  </Row>
                  <Row style={{ marginTop: '15px' }}>
                    <Col md={12}>
                      <label className={'input-label m-0'}>
                        Valor confirmado pelo proprietário do cartão
                      </label>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>{chargeBack.cardValidation.validated ? 'SIM' : 'NÃO'}</Col>
                  </Row>
                  <Row style={{ marginTop: '15px' }}>
                    <Col md={12}>
                      <label className={'input-label m-0'}>
                        Data da confirmação do valor na fatura
                      </label>
                    </Col>
                  </Row>
                  <Row>
                    <Col md={12}>
                      {chargeBack.cardValidation.validateDate
                        ? dayjs(chargeBack.cardValidation.validateDate)
                            .locale('pt-br')
                            .format('DD/MM/YYYY [às] HH:mm:ss.SSS')
                        : 'N/A'}
                    </Col>
                  </Row>
                </Fragment>
              )}
              {chargeBack.transfers && chargeBack.transfers.length > 0 && (
                <Fragment>
                  <Row style={{ marginTop: '30px' }}>
                    <Col md={12}>
                      <h6>Informações de transferência de ingresso</h6>
                      <hr />
                    </Col>
                  </Row>

                  {chargeBack.transfers.map(order => (
                    <Fragment key={order.id}>
                      <Row>
                        <Col md={4}>
                          <Row>
                            <Col md={12}>
                              <label className={'input-label m-0'}>Data Transferência</label>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={12}>
                              {dayjs(order.date).locale('pt-br').format('DD/MM/YYYY [às] HH:mm')}
                            </Col>
                          </Row>
                        </Col>
                        <Col md={4}>
                          <Row>
                            <Col md={12}>
                              <label className={'input-label m-0'}>Nome</label>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={12}>{order.client.clientName}</Col>
                          </Row>
                        </Col>
                        <Col md={4}>
                          <Row>
                            <Col md={12}>
                              <label className={'input-label m-0'}>CPF</label>
                            </Col>
                          </Row>
                          <Row>
                            <Col md={12}>{ofuscateAndMaskCPF(order.client.clientCpf)}</Col>
                          </Row>
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12}>
                          <CustomTable
                            columns={transferColumns}
                            data={order.itens.map(data => ({
                              event: data.event,
                              section: data.section,
                              ticket: data.ticket,
                              used: data.validatedDate
                                ? dayjs(data.validatedDate)
                                    .locale('pt-br')
                                    .format('DD/MM/YYYY [às] HH:mm')
                                : 'Não',
                            }))}
                            numberRowsPerPage={order.itens.length}
                            progressPending={false}
                          />
                        </Col>
                      </Row>
                    </Fragment>
                  ))}
                </Fragment>
              )}
            </div>
            <Button type="button" onClick={onExport} title="Exportar" />
          </Fragment>
        )}
      </Container>
    </Fragment>
  );
};
